import { useChartColors } from '../lib/accessibility'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, CheckCircle2, PawPrint, Stethoscope, Activity } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { api } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', bgRaised: 'var(--color-bg-card)', bgCard: '#1A2212',
  border: 'var(--color-border)', amber: 'var(--color-primary)', green: '#2D6A2F',
  text: 'var(--color-text)', textMuted: 'var(--color-text-muted)', textFaint: 'var(--color-text-faint)',
  red: '#F87171', blue: '#60A5FA',
}

interface Animal {
  id: string; name: string; species: string; breed?: string; tag_id?: string
  status: string; sex?: string; birth_date?: string; notes?: string; is_group?: boolean
}

interface HealthEvent {
  id: string; animal_id: string; animal_name?: string; event_type: string
  date: string; notes?: string; vet_name?: string; next_due?: string
}

const SPECIES_EMOJI: Record<string, string> = {
  chicken: '🐔', goat: '🐐', pig: '🐖', duck: '🦆', cow: '🐄', sheep: '🐑', other: '🐾',
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px',
  color: C.text, fontSize: 13, outline: 'none',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 500, color: C.textMuted, marginBottom: 5,
}

function AddAnimalModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ name: '', species: 'chicken', breed: '', tag_id: '', status: 'active', sex: '', birth_date: '', notes: '', is_group: false })
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post('/animals', d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['animals'] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) { e.preventDefault(); mutation.mutate({ ...form }) }
  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 460, maxWidth: '95vw', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Add Animal</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div style={{ fontSize: 15, fontWeight: 600 }}>Animal added!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 2 }}><label style={labelStyle}>Name / Identifier *</label><input required style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} /></div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Species</label>
                <select style={inputStyle} value={form.species} onChange={e => set('species', e.target.value)}>
                  {['chicken','goat','pig','duck','cow','sheep','other'].map(s => <option key={s} value={s}>{SPECIES_EMOJI[s]} {s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Breed</label><input style={inputStyle} value={form.breed} onChange={e => set('breed', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Tag ID</label><input style={inputStyle} value={form.tag_id} onChange={e => set('tag_id', e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Sex</label>
                <select style={inputStyle} value={form.sex} onChange={e => set('sex', e.target.value)}>
                  <option value="">Unknown</option><option value="male">Male</option><option value="female">Female</option>
                </select>
              </div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Birth Date</label><input type="date" style={inputStyle} value={form.birth_date} onChange={e => set('birth_date', e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="is_group" checked={form.is_group} onChange={e => set('is_group', e.target.checked)} />
              <label htmlFor="is_group" style={{ fontSize: 13, color: C.textMuted, cursor: 'pointer' }}>Track as group (flock/herd)</label>
            </div>
            <div><label style={labelStyle}>Notes</label><textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Add Animal'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

function LogVetEventModal({ animalId, onClose }: { animalId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ event_type: 'checkup', date: new Date().toISOString().slice(0, 10), vet_name: '', notes: '', next_due: '' })
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post(`/animals/${animalId}/health-events`, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['animal-health'] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) { e.preventDefault(); mutation.mutate({ ...form }) }
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 420, maxWidth: '95vw', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Log Vet Event</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div style={{ fontSize: 15, fontWeight: 600 }}>Event logged!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label style={labelStyle}>Event Type</label>
              <select style={inputStyle} value={form.event_type} onChange={e => set('event_type', e.target.value)}>
                {['checkup','vaccination','deworming','injury','illness','surgery','medication','other'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={form.date} onChange={e => set('date', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Next Due</label><input type="date" style={inputStyle} value={form.next_due} onChange={e => set('next_due', e.target.value)} /></div>
            </div>
            <div><label style={labelStyle}>Vet Name</label><input style={inputStyle} value={form.vet_name} onChange={e => set('vet_name', e.target.value)} /></div>
            <div><label style={labelStyle}>Notes</label><textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Log Event'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

export function AnimalsPage() {
  const chartC = useChartColors()
  const [showAdd, setShowAdd] = useState(false)
  const [selectedAnimal, setSelectedAnimal] = useState<Animal | null>(null)
  const [showVet, setShowVet] = useState<string | null>(null)

  const { data: animals = [], isLoading, isError, refetch } = useQuery<Animal[]>({
    queryKey: ['animals'],
    queryFn: () => api.get('/animals').then(r => r.data?.animals ?? r.data ?? []),
  })

  const { data: healthEvents = [], isLoading: healthLoading } = useQuery<HealthEvent[]>({
    queryKey: ['animal-health', selectedAnimal?.id],
    queryFn: () => api.get(`/animals/${selectedAnimal?.id}/health-events`).then(r => r.data?.health_events ?? r.data ?? []),
    enabled: !!selectedAnimal,
  })

  const allHealthEvents = useQuery<HealthEvent[]>({
    queryKey: ['animal-health-all'],
    queryFn: () => api.get('/animals/health-events').then(r => r.data?.health_events ?? r.data ?? []).catch(() => []),
  })

  const allEvents = allHealthEvents.data ?? []
  const activeAnimals = animals.filter(a => a.status === 'active').length
  const speciesCount = new Set(animals.map(a => a.species)).size

  // upcoming vet: health events with next_due within 30 days from today
  const upcomingVet = allEvents.filter(ev => {
    if (!ev.next_due) return false
    const due = new Date(ev.next_due).getTime()
    const now = Date.now()
    return due >= now && due <= now + 30 * 86_400_000
  }).length

  // Species distribution for chart
  const speciesChartData = Object.entries(
    animals.reduce<Record<string, number>>((acc, a) => { acc[a.species] = (acc[a.species] ?? 0) + 1; return acc }, {})
  ).map(([species, count]) => ({ species: species.charAt(0).toUpperCase() + species.slice(1), count }))
    .sort((a, b) => b.count - a.count)

  // Health event type distribution
  const eventTypeData = Object.entries(
    allEvents.reduce<Record<string, number>>((acc, ev) => { acc[ev.event_type] = (acc[ev.event_type] ?? 0) + 1; return acc }, {})
  ).map(([type, count]) => ({ type: type.charAt(0).toUpperCase() + type.slice(1), count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6)

  return (
    <>
      {showAdd && <AddAnimalModal onClose={() => setShowAdd(false)} />}
      {showVet && <LogVetEventModal animalId={showVet} onClose={() => setShowVet(null)} />}

      <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Animals</h1>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textMuted }}>Livestock inventory and veterinary records</p>
          </div>
          <button onClick={() => setShowAdd(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={16} /> Add Animal
          </button>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total animals', value: animals.length, icon: PawPrint, color: C.amber },
            { label: 'Active', value: activeAnimals, icon: Activity, color: '#4ADE80' },
            { label: 'Species', value: speciesCount, icon: PawPrint, color: C.blue },
            { label: 'Vet events due (30d)', value: upcomingVet, icon: Stethoscope, color: upcomingVet > 0 ? C.amber : C.textMuted },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Icon size={14} color={color} />
                <span style={{ fontSize: 11, color: C.textMuted }}>{label}</span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{value}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          {/* Left: Animal Cards */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: C.textFaint }}>Loading animals…</div>
            ) : isError ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <p style={{ color: C.red, marginBottom: 12 }}>Failed to load animals</p>
                <button onClick={() => refetch()} style={{ padding: '8px 16px', borderRadius: 8, background: C.bgCard, border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer' }}>Retry</button>
              </div>
            ) : animals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <PawPrint size={40} style={{ color: C.textFaint, opacity: 0.4, marginBottom: 12 }} />
                <p style={{ margin: 0, color: C.textFaint }}>No animals added yet</p>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: C.textFaint }}>Click "Add Animal" to start tracking your livestock</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {animals.map(a => (
                  <div
                    key={a.id}
                    onClick={() => setSelectedAnimal(selectedAnimal?.id === a.id ? null : a)}
                    style={{
                      background: selectedAnimal?.id === a.id ? 'color-mix(in srgb, var(--color-primary) 6%, transparent)' : C.bgCard,
                      border: `1px solid ${selectedAnimal?.id === a.id ? 'color-mix(in srgb, var(--color-primary) 27%, transparent)' : C.border}`,
                      borderRadius: 10, padding: '14px 18px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 14,
                    }}
                  >
                    <div style={{ fontSize: 28, flexShrink: 0 }}>{SPECIES_EMOJI[a.species] ?? '🐾'}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{a.name}</div>
                      <div style={{ fontSize: 12, color: C.textMuted }}>
                        {a.breed || a.species} {a.tag_id ? `· Tag: ${a.tag_id}` : ''} {a.is_group ? '· Group' : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: a.status === 'active' ? '#4ADE8022' : 'var(--color-bg-hover)', color: a.status === 'active' ? '#4ADE80' : C.textFaint, fontWeight: 600 }}>
                        {a.status}
                      </span>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); setShowVet(a.id) }}
                      style={{ padding: '5px 10px', borderRadius: 7, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}
                    >
                      Log Event
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Vet Events Sidebar */}
          <div style={{ width: 280, flexShrink: 0 }}>
            <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '0 16px 12px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textFaint, padding: '12px 0 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Stethoscope size={12} />
                {selectedAnimal ? `${selectedAnimal.name} — Vet History` : 'Recent Vet Events'}
              </div>
              {selectedAnimal ? (
                healthLoading ? (
                  <div style={{ fontSize: 12, color: C.textFaint, padding: '8px 0' }}>Loading…</div>
                ) : healthEvents.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.textFaint, padding: '8px 0' }}>No vet events for {selectedAnimal.name}</div>
                ) : (
                  healthEvents.map(ev => (
                    <div key={ev.id} style={{ padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{ev.event_type}</span>
                        <span style={{ fontSize: 11, color: C.textFaint }}>{new Date(ev.date).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                      </div>
                      {ev.vet_name && <div style={{ fontSize: 11, color: C.textMuted }}>{ev.vet_name}</div>}
                      {ev.next_due && <div style={{ fontSize: 11, color: C.amber }}>Next: {new Date(ev.next_due).toLocaleDateString()}</div>}
                    </div>
                  ))
                )
              ) : (
                allHealthEvents.isLoading ? (
                  <div style={{ fontSize: 12, color: C.textFaint, padding: '8px 0' }}>Loading…</div>
                ) : (allHealthEvents.data ?? []).length === 0 ? (
                  <div style={{ fontSize: 12, color: C.textFaint, padding: '8px 0' }}>No vet events logged yet. Select an animal or click "Log Event" to start.</div>
                ) : (
                  (allHealthEvents.data ?? []).slice(0, 8).map(ev => (
                    <div key={ev.id} style={{ padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{ev.animal_name ?? 'Animal'} — {ev.event_type}</span>
                        <span style={{ fontSize: 11, color: C.textFaint }}>{new Date(ev.date).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                      </div>
                      {ev.next_due && <div style={{ fontSize: 11, color: C.amber }}>Next: {new Date(ev.next_due).toLocaleDateString()}</div>}
                    </div>
                  ))
                )
              )}
              <button
                onClick={() => animals.length > 0 ? setShowVet(selectedAnimal?.id ?? animals[0].id) : undefined}
                style={{ width: '100%', marginTop: 12, padding: '7px 0', borderRadius: 7, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <Plus size={12} /> Log Vet Event
              </button>
            </div>

            {/* Species distribution chart */}
            {speciesChartData.length > 0 && (
              <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px', marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textFaint, marginBottom: 12 }}>By Species</div>
                <ResponsiveContainer width="100%" height={speciesChartData.length * 32 + 8}>
                  <BarChart data={speciesChartData} layout="vertical" margin={{ top: 0, right: 24, bottom: 0, left: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="species" tick={{ fill: chartC.muted, fontSize: 12 }} axisLine={false} tickLine={false} width={72} />
                    <Tooltip contentStyle={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={14}>
                      {speciesChartData.map((_, i) => (
                        <Cell key={i} fill={[chartC.accent, '#4ADE80', C.blue, '#E91E63', '#FF9800', '#9C27B0'][i % 6]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Vet event type breakdown */}
            {eventTypeData.length > 0 && (
              <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px', marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textFaint, marginBottom: 12 }}>Event Types</div>
                <ResponsiveContainer width="100%" height={eventTypeData.length * 32 + 8}>
                  <BarChart data={eventTypeData} layout="vertical" margin={{ top: 0, right: 24, bottom: 0, left: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="type" tick={{ fill: chartC.muted, fontSize: 12 }} axisLine={false} tickLine={false} width={80} />
                    <Tooltip contentStyle={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <Bar dataKey="count" fill={C.blue + 'AA'} radius={[0, 4, 4, 0]} barSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  )
}
